'use client';

import Image from 'next/image';
import Link from '@eliadmin/core/src/components/link';

import { FOOTER_CONFIG } from '../../constants/footer-config';
import { Paragraph } from '../ui/paragraph';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { getSafeHref } from '@eliadmin/core/src/utils/url-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import React from 'react';
import { ButtonIcon } from '../ui/button-icon';
import type {
  GeneralLayout,
  Menu,
} from '@eliadmin/core/src/api/general/types/general-layout-types';

export function MobileFooter({
  generalLayout,
  menus,
}: {
  generalLayout: GeneralLayout['data'];
  menus: Menu[];
}) {
  return (
    <div className="flex w-full flex-col items-start justify-start gap-6 sm:hidden">
      <Accordion
        type="multiple"
        className={cn(
          'flex w-full flex-col items-start justify-start gap-4',
          'ea-mobile-footer__accordion',
        )}
      >
        {menus.map(menu => (
          <React.Fragment key={menu.name}>
            <AccordionItem
              value={menu.name}
              className="flex w-full flex-col items-start justify-start gap-0 border-none"
            >
              <AccordionTrigger className="flex w-full items-center justify-between gap-4 p-0 text-left">
                <span className="h5">{menu.name}</span>
              </AccordionTrigger>
              <AccordionContent className="flex w-full flex-col items-start justify-start gap-3 p-0 pt-3">
                {menu.fields.map(field => (
                  <Link
                    key={field.name}
                    prefetch={false}
                    href={getSafeHref(field.path ?? '#')}
                    className="generic-hover text-sm tracking-tight"
                  >
                    {field.name}
                  </Link>
                ))}
              </AccordionContent>
            </AccordionItem>
            <div className="h-[1px] w-full bg-border" />
          </React.Fragment>
        ))}
        <AccordionItem
          value={'contact'}
          className="flex w-full flex-col items-start justify-start gap-0 border-none"
        >
          <AccordionTrigger className="flex w-full items-center justify-between gap-4 p-0 text-left">
            <span className="h5">Kontaktné údaje</span>
          </AccordionTrigger>
          <AccordionContent className="flex w-full flex-col items-start justify-start gap-3 p-0 pt-3">
            <Paragraph size="lg">
              <strong className="font-semibold">Infolinka</strong> (8:00 - 16:30)
            </Paragraph>
            <Link
              prefetch={false}
              href={`tel:${generalLayout?.contact?.phone?.replace(/\s+/g, '')}`}
              className={cn(
                'generic-hover',
                'flex items-center justify-start gap-2 text-sm font-semibold text-primary lg:text-base',
              )}
            >
              <ButtonIcon alt="Zavolať" url="/images/icons/phone-primary.svg" />
              {generalLayout?.contact?.phone}
            </Link>
            <Link
              prefetch={false}
              href={`mailto:${generalLayout?.contact?.email}`}
              className={cn(
                'generic-hover',
                'flex items-center justify-start gap-2 text-sm font-semibold text-primary lg:text-base',
              )}
            >
              <ButtonIcon alt="Email" url="/images/icons/email-primary.svg" />
              {generalLayout?.contact?.email}
            </Link>
            <div
              className={cn(
                'flex items-start justify-start gap-2 text-sm font-semibold text-primary lg:text-base',
              )}
            >
              <ButtonIcon alt="Lokalita" url="/images/icons/location-primary.svg" />
              {generalLayout?.contact?.address} {generalLayout?.contact?.postcode}{' '}
              {generalLayout?.contact?.city}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
      <div className="flex flex-col items-start justify-start gap-4">
        <div className="flex items-center justify-start gap-2">
          {FOOTER_CONFIG.payment_methods.map(method => (
            <Image
              key={method.name}
              src={method.icon}
              alt={method.name}
              width={54}
              height={28}
              className="shrink-0 object-contain"
            />
          ))}
        </div>
        <div className="flex items-center justify-start gap-2">
          <Image
            src={'/images/icons/lock-password-foreground.svg'}
            alt="Záruka bezpečného nákupu"
            width={16}
            height={16}
            className="shrink-0"
          />
          <Paragraph size="base" color="muted-foreground">
            Záruka bezpečného nákupu
          </Paragraph>
        </div>
      </div>
    </div>
  );
}
